import { NavLink, useLocation } from 'react-router-dom'
import {
  INTELLIGENCE_CONTEXT_NAV,
  PUBLICATIONS_CONTEXT_NAV,
  SETTINGS_CONTEXT_NAV,
  isContextualNavItemActive,
  type ContextualNavItem,
} from './navigation'
import type { ShellLayoutConfig, ShellUtilityItem } from './ShellLayoutContext'
import { IconIntelligence, IconPublication, IconSettings } from './icons'

function ContextualNavList({
  items,
  ariaLabel,
}: {
  items: ContextualNavItem[]
  ariaLabel: string
}) {
  const location = useLocation()
  return (
    <nav aria-label={ariaLabel}>
      <ul className="sources-filter-list">
        {items.map((item) => {
          const active = isContextualNavItemActive(location.pathname, item)
          return (
            <li key={item.id}>
              <NavLink
                to={item.to}
                end={item.exactMatch || item.to === '/publications'}
                className={() =>
                  active ? 'is-active settings-nav-link' : 'settings-nav-link'
                }
                aria-current={active ? 'page' : undefined}
                data-testid={`context-nav-${item.id}`}
                data-availability={item.availability}
              >
                {item.label}
                {item.availability === 'planned' ? (
                  <span className="muted"> · Planned</span>
                ) : null}
              </NavLink>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}

export function IntelligenceContextPanel() {
  return (
    <ContextualNavList items={INTELLIGENCE_CONTEXT_NAV} ariaLabel="Intelligence sections" />
  )
}

export function PublicationsContextPanel() {
  return (
    <ContextualNavList items={PUBLICATIONS_CONTEXT_NAV} ariaLabel="Publications sections" />
  )
}

export function SettingsContextPanel() {
  return <ContextualNavList items={SETTINGS_CONTEXT_NAV} ariaLabel="Settings sections" />
}

const intelligenceUtilityItems: ShellUtilityItem[] = [
  {
    id: 'sections',
    label: 'Sections',
    icon: <IconIntelligence />,
    opensFlyout: true,
  },
]

const publicationsUtilityItems: ShellUtilityItem[] = [
  {
    id: 'sections',
    label: 'Sections',
    icon: <IconPublication />,
    opensFlyout: true,
  },
]

const settingsUtilityItems: ShellUtilityItem[] = [
  {
    id: 'sections',
    label: 'Sections',
    icon: <IconSettings />,
    opensFlyout: true,
  },
]

/** Shell config fragment for Intelligence workspace pages (Stage 19H). */
export function intelligenceWorkspaceShell(
  overrides: Partial<ShellLayoutConfig> = {},
): ShellLayoutConfig {
  return {
    workspaceMode: 'canvas',
    workspaceTitle: 'Intelligence',
    contextRail: 'collapsed',
    contextPanelTitle: 'Intelligence sections',
    intelligenceRail: 'hidden',
    utilityRail: 'visible',
    utilityItems: intelligenceUtilityItems,
    contextPanel: <IntelligenceContextPanel />,
    detailPanel: null,
    panelKey: 'intelligence-nav',
    ...overrides,
  }
}

/** Shell config fragment for Publications workspace pages. */
export function publicationsWorkspaceShell(
  overrides: Partial<ShellLayoutConfig> = {},
): ShellLayoutConfig {
  return {
    workspaceMode: 'list',
    workspaceTitle: 'Publications',
    contextRail: 'collapsed',
    contextPanelTitle: 'Publications sections',
    intelligenceRail: 'hidden',
    utilityRail: 'visible',
    utilityItems: publicationsUtilityItems,
    contextPanel: <PublicationsContextPanel />,
    detailPanel: null,
    panelKey: 'publications-nav',
    ...overrides,
  }
}

/** Shell config fragment for Settings workspace pages (including /integrations, /system). */
export function settingsWorkspaceShell(
  overrides: Partial<ShellLayoutConfig> = {},
): ShellLayoutConfig {
  return {
    workspaceMode: 'list',
    workspaceTitle: 'Settings',
    contextRail: 'collapsed',
    contextPanelTitle: 'Settings sections',
    intelligenceRail: 'hidden',
    utilityRail: 'visible',
    utilityItems: settingsUtilityItems,
    contextPanel: <SettingsContextPanel />,
    detailPanel: null,
    panelKey: 'settings-nav',
    ...overrides,
  }
}
